import { useEffect, useRef, useState } from "react";
import { EditorContent, useEditor, useEditorState, type Editor } from "@tiptap/react";
import { Mark, mergeAttributes } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Blockquote from "@tiptap/extension-blockquote";
import { Placeholder } from "@tiptap/extensions";
import {
  Bold,
  ChevronsDownUp,
  Code,
  EyeOff,
  Italic,
  Link,
  Redo2,
  RemoveFormatting,
  SquareCode,
  Strikethrough,
  TextQuote,
  Underline,
  Undo2,
} from "lucide-react";
import { sanitizeTgHtml } from "@kickwin/shared";
import { editorToTgHtml, tgHtmlToEditor, type DocNode } from "@/lib/tg-format";
import { cn } from "@/lib/utils";
import { Button, Input } from "./primitives";

/**
 * The formatted-text editor (decision 45): exactly the formatting Telegram
 * supports — bold, italic, underline, strikethrough, spoiler, link, inline
 * code, code block, quote and expandable quote — and nothing Telegram would
 * drop, such as headings or lists. The value in and out is Telegram HTML.
 */

const Spoiler = Mark.create({
  name: "spoiler",
  parseHTML: () => [{ tag: "tg-spoiler" }, { tag: "span.tg-spoiler" }],
  renderHTML: ({ HTMLAttributes }) => [
    "span",
    mergeAttributes(HTMLAttributes, { class: "tg-spoiler" }),
    0,
  ],
});

/** A quote that Telegram shows collapsed until the player taps it. */
const Quote = Blockquote.extend({
  addAttributes() {
    return {
      expandable: {
        default: false,
        parseHTML: (el: HTMLElement) => el.hasAttribute("expandable"),
        renderHTML: (attrs: { expandable?: boolean }) =>
          attrs.expandable ? { "data-expandable": "" } : {},
      },
    };
  },
});

function extensions(placeholder: string | undefined) {
  return [
    StarterKit.configure({
      heading: false,
      bulletList: false,
      orderedList: false,
      listItem: false,
      listKeymap: false,
      horizontalRule: false,
      blockquote: false,
      trailingNode: false,
      link: {
        openOnClick: false,
        autolink: true,
        defaultProtocol: "https",
        protocols: ["tg"],
        isAllowedUri: (url) => /^(https?|tg):\/\//i.test(url),
      },
    }),
    Quote,
    Spoiler,
    Placeholder.configure({ placeholder: placeholder ?? "" }),
  ];
}

export function RichText({
  value,
  onChange,
  placeholder,
  disabled,
  compact,
  label,
}: {
  /** Telegram HTML. */
  value: string;
  onChange: (html: string) => void;
  placeholder?: string | undefined;
  disabled?: boolean | undefined;
  /** A shorter box, for a one-line answer. */
  compact?: boolean | undefined;
  /** The accessible name of the text box. */
  label?: string | undefined;
}) {
  // What this editor last reported, so an echo of it is not loaded back in
  // (which would move the cursor) but a change from outside is.
  const emitted = useRef(value);

  const editor = useEditor({
    extensions: extensions(placeholder),
    content: tgHtmlToEditor(value),
    editable: !disabled,
    editorProps: {
      attributes: {
        class: cn("tg-content tg-editor px-3 py-2 outline-none", compact ? "min-h-16" : "min-h-28"),
        ...(label ? { "aria-label": label } : {}),
        role: "textbox",
        "aria-multiline": "true",
      },
    },
    onUpdate: ({ editor: e }) => {
      const html = editorToTgHtml(e.getJSON() as DocNode);
      emitted.current = html;
      onChange(html);
    },
  });

  useEffect(() => {
    if (!editor || value === emitted.current) return;
    emitted.current = value;
    editor.commands.setContent(tgHtmlToEditor(value), { emitUpdate: false });
  }, [editor, value]);

  useEffect(() => {
    // No update event: nothing was typed.
    editor?.setEditable(!disabled, false);
  }, [editor, disabled]);

  return (
    <div
      // Inside a <label>, a click would otherwise also press the first
      // toolbar button.
      onClick={(e) => e.preventDefault()}
      className={cn(
        "rounded-md border border-input bg-card shadow-xs transition-[color,box-shadow] focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50",
        disabled && "opacity-60",
      )}
    >
      {editor && <Toolbar editor={editor} disabled={disabled} />}
      <EditorContent editor={editor} />
    </div>
  );
}

function Toolbar({ editor, disabled }: { editor: Editor; disabled?: boolean | undefined }) {
  const state = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e.isActive("bold"),
      italic: e.isActive("italic"),
      underline: e.isActive("underline"),
      strike: e.isActive("strike"),
      spoiler: e.isActive("spoiler"),
      link: e.isActive("link"),
      code: e.isActive("code"),
      codeBlock: e.isActive("codeBlock"),
      quote: e.isActive("blockquote"),
      expandable: e.isActive("blockquote", { expandable: true }),
      language: String(e.getAttributes("codeBlock").language ?? ""),
      href: String(e.getAttributes("link").href ?? ""),
      canUndo: e.can().undo(),
      canRedo: e.can().redo(),
    }),
  });
  const [linking, setLinking] = useState<string | null>(null);

  const chain = () => editor.chain().focus();
  const toggleQuote = (expandable: boolean) => {
    if (state.quote && state.expandable === expandable) {
      chain().lift("blockquote").run();
    } else if (state.quote) {
      chain().updateAttributes("blockquote", { expandable }).run();
    } else {
      chain().wrapIn("blockquote", { expandable }).run();
    }
  };

  const applyLink = () => {
    const href = (linking ?? "").trim();
    if (!href) chain().extendMarkRange("link").unsetLink().run();
    else {
      const full = /^(https?|tg):\/\//i.test(href) ? href : `https://${href}`;
      chain().extendMarkRange("link").setLink({ href: full }).run();
    }
    setLinking(null);
  };

  return (
    <div className="border-b border-border">
      <div
        role="toolbar"
        aria-label="Formatting"
        className="flex flex-wrap items-center gap-0.5 px-1.5 py-1"
      >
        <Tool
          label="Bold"
          active={state.bold}
          disabled={disabled}
          onClick={() => chain().toggleBold().run()}
        >
          <Bold />
        </Tool>
        <Tool
          label="Italic"
          active={state.italic}
          disabled={disabled}
          onClick={() => chain().toggleItalic().run()}
        >
          <Italic />
        </Tool>
        <Tool
          label="Underline"
          active={state.underline}
          disabled={disabled}
          onClick={() => chain().toggleUnderline().run()}
        >
          <Underline />
        </Tool>
        <Tool
          label="Strikethrough"
          active={state.strike}
          disabled={disabled}
          onClick={() => chain().toggleStrike().run()}
        >
          <Strikethrough />
        </Tool>
        <Tool
          label="Spoiler (hidden until tapped)"
          active={state.spoiler}
          disabled={disabled}
          onClick={() => chain().toggleMark("spoiler").run()}
        >
          <EyeOff />
        </Tool>
        <Tool
          label="Link"
          active={state.link || linking !== null}
          disabled={disabled}
          onClick={() => setLinking(linking === null ? state.href : null)}
        >
          <Link />
        </Tool>
        <Sep />
        <Tool
          label="Code"
          active={state.code}
          disabled={disabled}
          onClick={() => chain().toggleCode().run()}
        >
          <Code />
        </Tool>
        <Tool
          label="Code block"
          active={state.codeBlock}
          disabled={disabled}
          onClick={() => chain().toggleCodeBlock().run()}
        >
          <SquareCode />
        </Tool>
        <Tool
          label="Quote"
          active={state.quote && !state.expandable}
          disabled={disabled}
          onClick={() => toggleQuote(false)}
        >
          <TextQuote />
        </Tool>
        <Tool
          label="Expandable quote (collapsed until tapped)"
          active={state.expandable}
          disabled={disabled}
          onClick={() => toggleQuote(true)}
        >
          <ChevronsDownUp />
        </Tool>
        <Sep />
        <Tool
          label="Clear formatting"
          disabled={disabled}
          onClick={() => chain().unsetAllMarks().clearNodes().run()}
        >
          <RemoveFormatting />
        </Tool>
        <span className="ml-auto flex">
          <Tool
            label="Undo"
            disabled={disabled || !state.canUndo}
            onClick={() => chain().undo().run()}
          >
            <Undo2 />
          </Tool>
          <Tool
            label="Redo"
            disabled={disabled || !state.canRedo}
            onClick={() => chain().redo().run()}
          >
            <Redo2 />
          </Tool>
        </span>
      </div>

      {linking !== null && (
        <div className="flex flex-wrap items-center gap-1.5 border-t border-border px-2 py-1.5">
          <Input
            autoFocus
            className="mono h-8 min-w-0 flex-1"
            value={linking}
            onChange={(e) => setLinking(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                applyLink();
              }
              if (e.key === "Escape") setLinking(null);
            }}
            placeholder="https://kickwin.site"
            aria-label="Link address"
          />
          <Button type="button" size="sm" onClick={applyLink}>
            {linking.trim() ? "Apply" : "Remove link"}
          </Button>
          <Button type="button" size="sm" variant="quiet" onClick={() => setLinking(null)}>
            Cancel
          </Button>
        </div>
      )}

      {state.codeBlock && (
        <div className="flex items-center gap-1.5 border-t border-border px-2 py-1.5 text-muted-foreground">
          <span className="text-xs">Language</span>
          <Input
            className="mono h-7 w-36"
            value={state.language}
            onChange={(e) =>
              editor
                .chain()
                .updateAttributes("codeBlock", {
                  language: e.target.value.replace(/[^A-Za-z0-9_+#.-]/g, "").slice(0, 30) || null,
                })
                .run()
            }
            placeholder="optional, e.g. python"
            aria-label="Code language"
          />
        </div>
      )}
    </div>
  );
}

function Tool({
  label,
  active,
  disabled,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  disabled?: boolean | undefined;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      title={label}
      disabled={disabled}
      // Keep the selection in the text while the button is pressed.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={cn(
        "grid size-8 place-items-center rounded text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-30 [&_svg]:size-4 [@media(pointer:coarse)]:size-10",
        active && "bg-accent text-accent-foreground",
      )}
    >
      {children}
    </button>
  );
}

function Sep() {
  return <span aria-hidden className="mx-0.5 h-5 w-px bg-border" />;
}

/**
 * Telegram HTML shown as the player would see it. Safe to render: the
 * sanitiser keeps only Telegram's own tags and rebuilds their attributes.
 */
export function TgHtml({ html, className }: { html: string; className?: string }) {
  return (
    <div
      className={cn("tg-content whitespace-pre-wrap [overflow-wrap:anywhere]", className)}
      dangerouslySetInnerHTML={{ __html: sanitizeTgHtml(html) }}
    />
  );
}
